import { useEffect, useRef, useState } from "react"
import { Camera as CameraIcon, Upload, X } from "lucide-react"
import { ensureCameraPermission } from "../../utils/devicePermissions.js"
import { isNativeApp } from "../../utils/nativeApp.js"

const PHOTO_ACCEPT = "image/*,image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
const FILE_ACCEPT = `${PHOTO_ACCEPT},.pdf,application/pdf`
const CAPTURE_MQ = "(max-width: 1366px), (pointer: coarse)"

const isCaptureCancelled = (error) => {
  const msg = String(error?.message || error || "").toLowerCase()
  return msg.includes("cancel") || msg.includes("dismiss")
}

async function captureNativePhotoFile(namePrefix) {
  const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera")
  const photo = await Camera.getPhoto({
    quality: 85,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    correctOrientation: true,
    saveToGallery: false,
  })
  if (!photo?.webPath) return null
  const res = await fetch(photo.webPath)
  const blob = await res.blob()
  const format = String(photo.format || "jpeg").toLowerCase().replace("jpg", "jpeg")
  const ext = format === "jpeg" ? "jpg" : format
  return new File([blob], `${namePrefix}-${Date.now()}.${ext}`, {
    type: blob.type || `image/${format}`,
  })
}

/**
 * One required delivery photo. On phone / tablet, Photo opens the camera
 * the same way Add Shipment does. Upload picks a file from the gallery.
 */
const DeliveryProofCapture = ({
  label,
  required = false,
  file,
  preview,
  onFile,
  onError,
  inputId,
  hint,
  allowPdf = false,
}) => {
  const cameraRef = useRef(null)
  const galleryRef = useRef(null)
  const [showCapture, setShowCapture] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia(CAPTURE_MQ)
    const update = () => setShowCapture(mq.matches)
    update()
    mq.addEventListener("change", update)
    return () => mq.removeEventListener("change", update)
  }, [])

  const acceptFile = (picked) => {
    const next = picked?.[0]
    if (!next) return
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "image/heic", "image/heif"]
    const isPdf = next.type === "application/pdf" || /\.pdf$/i.test(next.name || "")
    const typeOk = isPdf
      ? allowPdf
      : (!next.type || allowed.includes(next.type) || next.type.startsWith("image/"))
    if (!typeOk) {
      onError?.(allowPdf
        ? "Invalid file type. Only JPG, PNG, GIF, WebP, and PDF are allowed."
        : "Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.")
      return
    }
    if (next.size > 5 * 1024 * 1024) {
      onError?.("File size too large. Maximum size is 5MB.")
      return
    }
    onFile?.(next)
  }

  const openCamera = () => {
    if (isNativeApp()) {
      void (async () => {
        const allowed = await ensureCameraPermission()
        if (!allowed) {
          onError?.("Camera permission denied. Enable camera access in app settings.")
          return
        }
        try {
          const captured = await captureNativePhotoFile(inputId || "delivery-proof")
          if (captured) acceptFile([captured])
        } catch (error) {
          if (isCaptureCancelled(error)) return
          cameraRef.current?.click()
        }
      })()
      return
    }
    cameraRef.current?.click()
  }

  return (
    <div className="min-w-0">
      <label className="block text-[10px] sm:text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      {hint ? <p className="text-[10px] sm:text-xs text-gray-500 mb-1 leading-snug">{hint}</p> : null}
      <input
        ref={cameraRef}
        id={inputId}
        type="file"
        accept={PHOTO_ACCEPT}
        capture="environment"
        className="sr-only"
        onChange={(e) => {
          acceptFile(e.target.files)
          e.target.value = ""
        }}
      />
      <input
        ref={galleryRef}
        type="file"
        accept={allowPdf ? FILE_ACCEPT : PHOTO_ACCEPT}
        className="sr-only"
        onChange={(e) => {
          acceptFile(e.target.files)
          e.target.value = ""
        }}
      />
      {showCapture ? (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={openCamera}
            className="min-h-[44px] px-2 py-2 text-xs font-semibold rounded-xl border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-1.5"
          >
            <CameraIcon className="w-4 h-4" />
            Photo
          </button>
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            className="min-h-[44px] px-2 py-2 text-xs font-semibold rounded-xl border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5"
          >
            <Upload className="w-4 h-4" />
            Upload
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => galleryRef.current?.click()}
          className="w-full min-h-[36px] px-2 py-1.5 text-xs sm:text-sm font-semibold rounded-xl border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
        >
          {file ? (allowPdf ? "Change file" : "Change photo") : (allowPdf ? "Choose photo or PDF" : "Choose photo")}
        </button>
      )}
      {file ? (
        <p className="mt-1 text-[10px] text-gray-500 truncate">{file.name}</p>
      ) : null}
      {file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || "")) ? (
        <p className="mt-2 inline-flex items-center rounded-lg border border-gray-300 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
          PDF attached
        </p>
      ) : preview ? (
        <div className="relative inline-block mt-2">
          <img
            src={preview}
            alt={label}
            className="h-24 sm:h-32 max-w-full object-cover rounded-xl border border-gray-300"
          />
          <button
            type="button"
            onClick={() => onFile?.(null)}
            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
            aria-label={`Remove ${label}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default DeliveryProofCapture
